import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  CardMedia,
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';
import { brandColors } from '../../theme';

interface SampleReportsModalProps {
  open: boolean;
  onClose: () => void;
}

interface ReportOption {
  title: string;
  description: string;
  imagePath: string;
  pdfPath: string;
}

const reportOptions: ReportOption[] = [
  {
    title: 'Standard Market Report',
    description: 'Comprehensive monthly analysis with market performance metrics, economic drivers, and historical trends for hotel classes.',
    imagePath: 'https://marketcompassdev.blob.core.windows.net/marketimages/markets/poland/warsaw-1.jpg',
    pdfPath: '/sample_reports/Sample-GlobalMarketReport.pdf',
  },
  {
    title: 'Customized Submarket Report',
    description: 'Detailed submarket analysis with performance overlays showing localized trends and opportunities within the broader market.',
    imagePath: '/images/market_subreport_950x720.jpg',
    pdfPath: '/sample_reports/Sample-GlobalMarketReportWithSubmarketOverlay.pdf',
  },
  {
    title: 'Customized Comp Set Report',
    description: 'Competitive set analysis comparing your property against selected competitors with detailed performance benchmarking.',
    imagePath: '/images/trend_report_950x720.jpg',
    pdfPath: '/sample_reports/Sample-GlobalMarketReportWithCompSetOverlay.pdf',
  },
];

export const SampleReportsModal: React.FC<SampleReportsModalProps> = ({
  open,
  onClose,
}) => {
  const handleDownload = (pdfPath: string, title: string) => {
    window.open(pdfPath, '_blank');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: brandColors.indigo }}>
            Sample Market Reports
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: brandColors.mediumGray,
              '&:hover': {
                color: brandColors.indigo,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" sx={{ color: brandColors.mediumGray, mt: 1 }}>
          Explore our different report types to see the depth and quality of insights we provide
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={3}>
          {reportOptions.map((report, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  border: `1px solid ${brandColors.lightGray}`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    borderColor: brandColors.gold,
                  },
                }}
              >
                <CardMedia
                  component="img"
                  image={report.imagePath}
                  alt={report.title}
                  sx={{
                    width: '100%',
                    aspectRatio: '950/720',
                    objectFit: 'contain',
                    backgroundColor: brandColors.lightGray,
                  }}
                  onError={(e) => {
                    // Fallback to a placeholder if image doesn't exist
                    (e.target as HTMLImageElement).src = '/images/trend_report_950x720.jpg';
                  }}
                />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: brandColors.indigo,
                      mb: 1.5,
                    }}
                  >
                    {report.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: brandColors.mediumGray,
                      lineHeight: 1.6,
                      mb: 2,
                      flexGrow: 1,
                    }}
                  >
                    {report.description}
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(report.pdfPath, report.title)}
                    sx={{
                      backgroundColor: brandColors.blue,
                      color: brandColors.white,
                      fontWeight: 600,
                      textTransform: 'none',
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: brandColors.indigo,
                      },
                    }}
                  >
                    Download Sample
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            mt: 4,
            p: 3,
            backgroundColor: `${brandColors.gold}15`,
            borderRadius: 2,
            borderLeft: `4px solid ${brandColors.gold}`,
          }}
        >
          <Typography variant="body2" sx={{ color: brandColors.indigo, fontWeight: 500, mb: 1 }}>
            Need a Custom Report?
          </Typography>
          <Typography variant="body2" sx={{ color: brandColors.mediumGray, lineHeight: 1.7 }}>
            With a Market Customization License, you can create personalized submarket overlays and
            competitive set reports tailored to your specific investment or operational needs.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
